import { Button, Page, TextStyle } from '@superops/ui-design-system';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AccountClient, GET_ALL_EMPLOYEE_LIST } from '@nile/graphql';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ALL_TABLES,
  SuperTable,
  TableContainer,
  getColumnWidth as superTableColumnWidth,
  getTableRowCount,
  getTranslatedText,
  getAllColumnWidth,
  isAuthorised,
  AppContext
} from '@nile/components';
import {
  checkDynamicQuery,
  getAsyncOptions,
  fetchValues,
  updateConditions,
  getDefaultValue,
  generateOptions
} from '@nile/banyan/src/Pebbles/IntegrationsHelper';
import {
  getAccessor,
  getType,
  isEditable
} from '@nile/banyan/src/Pages/Protected/Configuration/Pricing/helper';
import AvatarTextCell from '@nile/banyan/src/Components/Tables/AvatarTextCell';
import { SVGIcon } from '@superops/super-icons';
import { DELETE_EMPLOYEE } from '@nile/graphql/src/Query/employee';
import ActionPopover from '@nile/banyan/src/Pages/Protected/Configuration/Pricing/Cells/ActionPopover';

const ModifyTechnician = (props) => {
  const { closePage = () => {}, callBack } = props;

  const intl = useIntl();

  const tableName = ALL_TABLES.manageTechnician;
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const userRole = currentUser?.role?.roleFeatureList || [];
  const isTechAuthorised = isAuthorised(userRole, 'employees.delete');

  const ROW_COUNT = getTableRowCount(tableName);

  const options = {
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ],
    condition: null
  };

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);
  const [listInfo, setListInfo] = useState(options);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const columnWidthObject = getAllColumnWidth(tableName);
  const [viewConditions, setViewConditions] = useState(null);
  const conditionRef = useRef(1);
  conditionRef.current = viewConditions;

  const defaultColumnCells = {
    AvatarText: AvatarTextCell,
    Menu: ActionPopover
  };

  const deleteEmployee = (user, forget = false) => {
    AccountClient.mutate({
      mutation: DELETE_EMPLOYEE,
      variables: { ids: [user.userId], forget: forget }
    }).then((res) => {
      if (res?.data) {
        fetchViewData();
        if (callBack) {
          callBack();
        }
      }
    });
  };

  const updateAllConditions = (condition, selectedField) => {
    updateConditions({
      condition,
      selectedField,
      viewConditions: conditionRef.current,
      setConditions: setViewConditions
    });
  };

  const fetchViewData = async (_, pageOptions) => {
    setLoading(true);
    const requestOption = {
      input: {
        listInfo: pageOptions
          ? {
              page: pageOptions.page || listInfo?.page || options.page,
              pageSize:
                pageOptions.pageSize || listInfo?.pageSize || options.pageSize,
              sort: pageOptions.sort || null,
              condition: !!viewConditions ? viewConditions : null
            }
          : {
              ...options,
              condition: !!viewConditions ? viewConditions : null
            }
      }
    };
    await AccountClient.query({
      query: GET_ALL_EMPLOYEE_LIST,
      variables: requestOption,
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data) {
        setData(res?.data?.getTechnicianList?.userList);
        const {
          page,
          pageSize,
          totalCount,
          sort
        } = res?.data?.getTechnicianList?.listInfo;
        setListInfo({
          totalCount: totalCount,
          pageSize: pageSize,
          page: page,
          sort: sort
        });
      }
    });
    const cols = [
      {
        name: 'name',
        label: getTranslatedText(intl, 'admin.employee.name.title'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        size: 200,
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'role.name',
        label: getTranslatedText(intl, 'entity.role'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        size: 150,
        disableSortBy: false,
        disableFilters: true
      },
      {
        name: 'email',
        label: getTranslatedText(intl, 'admin.employee.email.title'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: false
      }
    ];
    const columns = cols.map((col, idx) => {
      return {
        Header: col.label,
        size: superTableColumnWidth(columnWidthObject, col.name, col?.size),
        minSize: 50,
        accessorKey: getAccessor(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        helpers: {
          getDefaultValue,
          updateConditions: updateAllConditions,
          checkDynamicQuery,
          getAsyncOptions,
          fetchValues,
          generateOptions
        },
        disableFilters: col?.disableFilters,
        disableSortBy: col?.disableSortBy,
        field: col
      };
    });
    if (isTechAuthorised) {
      columns.push({
        header: ' ',
        Header: ' ',
        accessorKey: 'value',
        width: 25,
        size: 25,
        pinned: 'right',
        type: 'Menu',
        actions: { delete: deleteEmployee },
        disableSortBy: true,
        disableFilters: true,
        labels: {
          deleteEmployee: {
            desc: 'client.user.delete.modal.desc'
          }
        }
      });
    }
    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    setLoading(false);
  };

  useEffect(() => {
    fetchViewData(null, viewConditions);
  }, [viewConditions]);

  useEffect(() => {
    fetchViewData();
  }, []);

  return (
    <>
      <Page
        contentHeight="100px"
        variant="seondary"
        divider
        fullWidth
        title={
          <div className="fx actr g10">
            <Button
              icon={<SVGIcon name="BackArrow" size="sm" />}
              onClick={() => closePage('TECHNICIAN')}
            />
            <TextStyle weight="Bold">
              <FormattedMessage id="app.purchase.manage.technician" />
            </TextStyle>
          </div>
        }
        primaryAction={
          <div className="fx g20">
            {viewConditions && (
              <Button plain onClick={() => setViewConditions(null)} size="tiny">
                <FormattedMessage id="attribute.reset" />
              </Button>
            )}
          </div>
        }
      >
        <div className="manageModal">
          <TableContainer>
            <SuperTable
              context={{}}
              fullHeight
              columns={columnGroup}
              data={data}
              rowCount={ROW_COUNT}
              refreshTable={fetchViewData}
              tableName={tableName}
              listInfo={listInfo}
              pagination={
                Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1
              }
              pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
              recordCount={listInfo.totalCount}
              defaultColumnCells={defaultColumnCells}
              activePage={listInfo.page || 0}
              isLoading={loading}
              noBulkSelect
            />
          </TableContainer>
        </div>
      </Page>
    </>
  );
};

export default ModifyTechnician;
